/**
 * Advanced Attributes Filter Component
 *
 * Displays category-specific attributes in an "ADVANCED" section.
 * Fetches attributes based on the current breadcrumb path.
 * Figma: AdvancedFilters (Node: 355:13130)
 */
"use client";

import React from "react";

import Image from "next/image";

import { isHiddenAdvancedAttributeKey } from "@/lib/filter-visibility";
import { getAttributesByFacets } from "@/lib/shop-agent-client";
import type { ShopperFilters } from "@/lib/shopper-search";
import { shouldShowAttributeForContext } from "@/utils/attributeVisibility";

import { Checkbox } from "./Checkbox";

import type {
  AttributeInfo,
  GetAttributeValuesResponse,
} from "../../../../openapi/shop_agent/client/types.gen";

interface AdvancedAttributesFilterProps {
  searchParams: ShopperFilters | null;
  selectedAttributes: Map<string, Set<string>>;
  attributeValueCounts?: Map<string, Map<string, number>>;
  onToggle: (attributeHandle: string, value: string) => void;
  className?: string;
}

// Progressive disclosure: show N attributes initially, then reveal more in increments
const INITIAL_VISIBLE_COUNT = 10;
const SHOW_MORE_INCREMENT = 5;

function getSortedCategoryFacets(
  facets: Array<{ name: string; values: string[] }>
): Array<{ name: string; values: string[] }> {
  return facets
    .filter((facet) => facet.name.startsWith("category_path.depth_"))
    .sort((a, b) => {
      const depthA = Number.parseInt(a.name.replace("category_path.depth_", ""), 10);
      const depthB = Number.parseInt(b.name.replace("category_path.depth_", ""), 10);
      return depthA - depthB;
    })
    .map((facet) => ({ name: facet.name, values: [...facet.values] }));
}

function getCategoryPathFromFacets(facets: Array<{ name: string; values: string[] }>): string[] {
  return getSortedCategoryFacets(facets)
    .map((facet) => (Array.isArray(facet.values) ? facet.values[0] : ""))
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0);
}

function getCategoryFacetKey(facets: Array<{ name: string; values: string[] }>): string {
  return facets.map((facet) => `${facet.name}:${facet.values.join("|")}`).join(";");
}

export function AdvancedAttributesFilter({
  searchParams,
  selectedAttributes,
  attributeValueCounts,
  onToggle,
  className = "",
}: AdvancedAttributesFilterProps) {
  const [allAttributes, setAllAttributes] = React.useState<AttributeInfo[] | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [expandedAttributes, setExpandedAttributes] = React.useState<Set<string>>(new Set());
  const [sectionExpanded, setSectionExpanded] = React.useState(false);
  const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_COUNT);

  const facets = React.useMemo(() => searchParams?.facets || [], [searchParams]);
  const categoryFacets = React.useMemo(() => getSortedCategoryFacets(facets), [facets]);
  const categoryFacetKey = React.useMemo(
    () => getCategoryFacetKey(categoryFacets),
    [categoryFacets]
  );
  const categoryFacetsRef = React.useRef<Array<{ name: string; values: string[] }>>([]);
  const lastFetchedCategoryKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    categoryFacetsRef.current = categoryFacets;
  }, [categoryFacets]);

  const categoryPath = React.useMemo(() => getCategoryPathFromFacets(facets), [facets]);

  const attributesForContext = React.useMemo(() => {
    if (!allAttributes || allAttributes.length === 0) return null;

    const filteredAttributes = allAttributes.filter(
      (attr) =>
        !isHiddenAdvancedAttributeKey(attr.handle) &&
        shouldShowAttributeForContext(attr.handle, {
          categoryPath,
          facets,
        })
    );

    return filteredAttributes.length > 0 ? filteredAttributes : null;
  }, [allAttributes, categoryPath, facets]);

  const attributes = React.useMemo(() => {
    if (!attributesForContext || attributesForContext.length === 0) return null;

    const filteredAttributes = attributesForContext
      .map((attr) => {
        const valueCounts = attributeValueCounts?.get(attr.handle);
        if (!valueCounts || !attr.possible_values) {
          return attr;
        }

        const possibleValues = attr.possible_values.filter((value) => {
          const valueHandle = (value as { handle?: string }).handle || value.value;
          return valueCounts.has(valueHandle) && (valueCounts.get(valueHandle) ?? 0) > 0;
        });

        return {
          ...attr,
          possible_values: possibleValues,
          value_count: possibleValues.length,
        };
      })
      .filter((attr) => !attr.possible_values || attr.possible_values.length > 0);

    return filteredAttributes.length > 0 ? filteredAttributes : null;
  }, [attributeValueCounts, attributesForContext]);

  // Reset paging and clear cached values when category context changes.
  // Clearing prevents stale attributes from flashing if the section is collapsed
  // during a category switch and then re-expanded.
  React.useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
    setAllAttributes(null);
    setIsLoading(false);
    lastFetchedCategoryKeyRef.current = null;
  }, [categoryFacetKey]);

  // Fetch aggregated attributes only when section is expanded and category context changes.
  // We intentionally keep previously fetched values while merely collapsing/expanding
  // within the same category to avoid unnecessary network calls.
  React.useEffect(() => {
    if (!sectionExpanded) {
      return;
    }

    if (!categoryFacetKey) {
      setAllAttributes(null);
      setIsLoading(false);
      lastFetchedCategoryKeyRef.current = null;
      return;
    }

    // Avoid refetching when users simply collapse/re-expand within the
    // same category context.
    if (lastFetchedCategoryKeyRef.current === categoryFacetKey) {
      return;
    }

    const fetchAttributes = async () => {
      setIsLoading(true);
      const requestedKey = categoryFacetKey;
      // Mark in-progress for this category key so rapid toggles don't duplicate calls.
      lastFetchedCategoryKeyRef.current = requestedKey;
      try {
        const response: GetAttributeValuesResponse = await getAttributesByFacets(
          categoryFacetsRef.current
        );

        // Ignore stale responses from superseded category contexts.
        if (lastFetchedCategoryKeyRef.current !== requestedKey) {
          return;
        }

        if (response.attributes && response.attributes.length > 0) {
          setAllAttributes(response.attributes);
        } else {
          setAllAttributes(null);
        }
      } catch (error) {
        if (lastFetchedCategoryKeyRef.current !== requestedKey) {
          return;
        }
        console.error("Failed to fetch aggregated attributes:", error);
        setAllAttributes(null);
        // Allow retry after failures (e.g., user re-expands section).
        lastFetchedCategoryKeyRef.current = null;
      } finally {
        if (lastFetchedCategoryKeyRef.current === requestedKey) {
          setIsLoading(false);
        }
      }
    };

    fetchAttributes();
  }, [sectionExpanded, categoryFacetKey]);

  const visibleAttributes = React.useMemo(
    () => attributes?.slice(0, visibleCount) ?? [],
    [attributes, visibleCount]
  );

  // Don't render only when search params are unavailable.
  // Keep the section visible even before attributes are fetched so users can expand it.
  if (!searchParams) {
    return null;
  }

  // When there are no category facets (e.g., catalog browse without a drilled-in category)
  // we still render the section header so users know the filter exists. The expanded state
  // surfaces a helpful message because the /by-facets endpoint requires category context
  // to return meaningful attributes.
  const hasCategoryContext = categoryFacets.length > 0;

  const toggleAttributeExpanded = (handle: string) => {
    setExpandedAttributes((prev) => {
      const next = new Set(prev);
      if (next.has(handle)) {
        next.delete(handle);
      } else {
        next.add(handle);
      }
      return next;
    });
  };

  return (
    <div
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "7.629e-6px", // Matches Figma exactly (essentially 0)
      }}
    >
      {/* Main "ADVANCED" Heading Row */}
      <div
        style={{
          display: "flex",
          height: "41.974px",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          cursor: "pointer",
        }}
        onClick={() => setSectionExpanded(!sectionExpanded)}
      >
        <div
          style={{
            height: "14.001px",
            width: "65.923px",
          }}
        >
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "10.5px",
              lineHeight: "14px",
              letterSpacing: "0.6173px",
              textTransform: "uppercase",
              color: "#000000",
              margin: 0,
              whiteSpace: "pre",
            }}
          >
            ADVANCED
          </p>
        </div>
        <div style={{ width: "14px", height: "14px", position: "relative" }}>
          <Image
            src={sectionExpanded ? "/icons/dropdown.svg" : "/icons/chevron.svg"}
            alt=""
            width={14}
            height={14}
          />
        </div>
      </div>

      {/* Attribute Rows */}
      {sectionExpanded && (
        <>
          {!hasCategoryContext ? (
            <div
              style={{
                display: "flex",
                minHeight: "41.974px",
                alignItems: "center",
                width: "100%",
                paddingBottom: "10px",
              }}
            >
              <p
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "16px",
                  color: "#6A7282",
                  margin: 0,
                }}
              >
                Select a category above to see advanced filters.
              </p>
            </div>
          ) : isLoading ? (
            <div
              style={{
                display: "flex",
                height: "41.974px",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ height: "14.001px" }}>
                <p
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "10.5px",
                    lineHeight: "14px",
                    letterSpacing: "0.6173px",
                    textTransform: "uppercase",
                    color: "#6A7282",
                    margin: 0,
                    whiteSpace: "pre",
                  }}
                >
                  Loading...
                </p>
              </div>
            </div>
          ) : attributes && attributes.length > 0 ? (
            <>
              {visibleAttributes.map((attr) => {
                const isExpanded = expandedAttributes.has(attr.handle);
                const hasValues = attr.possible_values && attr.possible_values.length > 0;

                return (
                  <React.Fragment key={attr.handle}>
                    {/* Attribute Row (e.g., "JACKET CLOSURE") */}
                    <div
                      style={{
                        display: "flex",
                        height: "41.974px",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        cursor: hasValues ? "pointer" : "default",
                      }}
                      onClick={() => hasValues && toggleAttributeExpanded(attr.handle)}
                    >
                      <div style={{ height: "14.001px" }}>
                        <p
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 500,
                            fontSize: "10.5px",
                            lineHeight: "14px",
                            letterSpacing: "0.6173px",
                            textTransform: "uppercase",
                            color: "#6A7282",
                            margin: 0,
                            whiteSpace: "pre",
                          }}
                        >
                          {attr.name}
                        </p>
                      </div>
                      {hasValues && (
                        <div
                          style={{
                            width: "14px",
                            height: "14px",
                            position: "relative",
                          }}
                        >
                          <Image
                            src={isExpanded ? "/icons/dropdown.svg" : "/icons/chevron.svg"}
                            alt=""
                            width={14}
                            height={14}
                          />
                        </div>
                      )}
                    </div>

                    {/* Expanded Values */}
                    {isExpanded && hasValues && attr.possible_values && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          paddingLeft: "0px",
                          paddingBottom: "10px",
                          paddingTop: "10px",
                          gap: "10px",
                        }}
                      >
                        {attr.possible_values.map((value) => {
                          const valueHandle = (value as { handle?: string }).handle || value.value;
                          const valueCount = attributeValueCounts
                            ?.get(attr.handle)
                            ?.get(valueHandle);
                          const isSelected =
                            selectedAttributes.get(attr.handle)?.has(valueHandle) || false;

                          return (
                            <div
                              key={valueHandle}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                height: "23.988px",
                                cursor: "pointer",
                              }}
                              onClick={() => onToggle(attr.handle, valueHandle)}
                            >
                              {/* Checkbox - 12px × 12px with padding */}
                              <div
                                style={{
                                  paddingTop: "3.996px",
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => onToggle(attr.handle, valueHandle)}
                                />
                              </div>

                              {/* Value Label */}
                              <div
                                style={{
                                  marginLeft: "23.988px", // Gap between checkbox and label
                                  paddingTop: "4.654px",
                                  flex: 1,
                                  minWidth: 0,
                                  textAlign: "left",
                                }}
                              >
                                <span className="text-action text-ink">{value.value}</span>
                              </div>
                              {typeof valueCount === "number" && (
                                <span className="text-caption text-ink-muted tabular-nums">
                                  {valueCount.toLocaleString()}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Show more / Show less toggle */}
              {attributes.length > INITIAL_VISIBLE_COUNT && (
                <div
                  style={{
                    display: "flex",
                    height: "41.974px",
                    alignItems: "center",
                    width: "100%",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (visibleCount >= attributes.length) {
                      setVisibleCount(INITIAL_VISIBLE_COUNT);
                    } else {
                      setVisibleCount((prev) =>
                        Math.min(prev + SHOW_MORE_INCREMENT, attributes.length)
                      );
                    }
                  }}
                >
                  <p
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      fontSize: "10.5px",
                      lineHeight: "14px",
                      letterSpacing: "0.6173px",
                      textTransform: "uppercase",
                      color: "#2563EB",
                      margin: 0,
                      whiteSpace: "pre",
                    }}
                  >
                    {visibleCount >= attributes.length
                      ? "Show less"
                      : `Show more (${attributes.length - visibleCount})`}
                  </p>
                </div>
              )}
            </>
          ) : null}
        </>
      )}

      {/* Bottom Divider */}
      <div
        style={{
          width: "100%",
          height: "0.882px",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
          marginTop: "0px",
        }}
      />
    </div>
  );
}
